/**
 * WHICH COUNTRIES THE CHECKUP IS ALLOWED TO ANSWER FOR.
 *
 * Peter, 2026-07-14: "The checkup app also still includes countries that we are not providing a
 * robust regulatory project management around yet. The checkup can only focus on Nigeria for now
 * and leave other countries like Kenya, South Africa, Zambia in a coming-soon state. If someone
 * clicks it, they should be allowed to say 'request this regulatory set' — so there's a mechanism
 * to get customer feedback around where Enereg should invest in developing its regulatory
 * knowledge bases next."
 *
 * The checkup was offering six countries and answering confidently for all of them. Only Nigeria
 * has been through the regulatory pipeline. A confident answer about Kenya, drawn from a seed
 * nobody has verified, is the same bluff as telling a mini-grid developer to start an EIA we
 * cannot say they need — just at the scale of a whole country.
 *
 * THIS FILE IS NOT A REGULATORY JUDGEMENT AND MUST NEVER BECOME ONE. It records a single fact
 * that the regulatory team owns: has this country's regulatory set been researched and verified
 * to the standard we are willing to put our name to? Nobody on the product side gets to promote a
 * country by deciding the seed "looks complete". A country moves to LIVE when the regulatory
 * research says it does, and not before.
 *
 * The "coming soon" state is not a dead end. It is the demand signal: the request each visitor
 * files tells us which regulatory set to build next, in their words, with their email attached.
 */
import type { CountryCode } from "../types/field.ts";

/**
 * Countries whose regulatory set has been researched, verified, and shipped through the
 * regulatory pipeline — the only ones the checkup will give an answer for.
 *
 * To add one: the regulatory team says so. Not because the seed has rows in it.
 */
export const LIVE_COUNTRIES = ["NG"] as const satisfies readonly CountryCode[];

/** Every country the product's pickers show, live or not. Order is the picker's order.
 *  Nigeria is live; Kenya and South Africa are the coming-soon set — the two markets we are
 *  prioritising next, shown greyed with a "request this regulatory set" action. Ghana, Namibia
 *  and Zambia have seed data but are NOT surfaced: an unverified regulatory set we cannot answer
 *  for should not sit in the picker collecting confused clicks (Peter, 2026-07-15).
 *  Kept as a narrow tuple (`satisfies`, not a wide annotation): the canonical CountryCode union
 *  spans 22 expansion markets, but the pickers only know these three — deriving
 *  `(typeof ALL_COUNTRIES)[number]` gives consumers the exact three-member union. */
export const ALL_COUNTRIES = ["NG", "KE", "ZA"] as const satisfies readonly CountryCode[];

/** The countries the product's pickers know about — a narrow slice of the canonical CountryCode. */
export type CheckupCountry = (typeof ALL_COUNTRIES)[number];

export function isLive(country: CountryCode): boolean {
  return (LIVE_COUNTRIES as readonly CountryCode[]).includes(country);
}

/** The country the checkup opens on. Must be live — the wizard's default may never be a country
 *  we cannot answer for. checkup-coverage.test.ts asserts this as a value, not by reading JSX. */
export const DEFAULT_CHECKUP_COUNTRY: CheckupCountry = "NG";

/** One email rule for the whole checkup surface (gate, request panel; src/index.ts has the
 *  server-side copy). Linear — no backtracking shape — and safe on any input length. */
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/** Caps for the request panel's free text. Enforced SERVER-side (lib/waitlist-server.ts is the
 *  only writer of the waitlist table); the client's maxLength attributes just keep the UI honest. */
export const REQUEST_EMAIL_MAX = 254;
export const REQUEST_NOTE_MAX = 2000;

/**
 * The waitlist row a "request this regulatory set" click records. Pure and testable: trims and
 * lowercases the email, tags the source, and carries the developer's own words as the research
 * brief. Returns null when the email fails validation — the caller must not insert.
 */
export function regulatoryRequestRow(email: string, country: CountryCode, note: string): {
  email: string;
  country: CountryCode;
  source: "regulatory_request";
  checkup_snapshot: { requested_country: CountryCode; note: string | null };
} | null {
  if (!isValidEmail(email)) return null;
  return {
    email: email.trim().toLowerCase().slice(0, REQUEST_EMAIL_MAX),
    country,
    source: "regulatory_request",
    checkup_snapshot: {
      requested_country: country,
      note: note.trim().slice(0, REQUEST_NOTE_MAX) || null,
    },
  };
}

/** The countries a visitor can ask us to build next. */
export const REQUESTABLE_COUNTRIES: readonly CheckupCountry[] = ALL_COUNTRIES.filter((c) => !isLive(c));

/**
 * What we say when someone picks a country we haven't done the work for. Deliberately plain:
 * we are not "launching soon", we have not built it yet, and we would rather say so than guess.
 */
export const COMING_SOON_BLURB =
  "We haven't finished the regulatory research for this country yet, and we would rather tell " +
  "you that than guess. Nigeria is live today. Tell us you need this one and we'll prioritise it " +
  "— your request is how we decide what to build next.";
